<template>
  <div
    class="row cursor-pointer justify-between selecting-mode items-start"
    :class="{ 'no-wrap q-gutter-x-sm': noWrap }"
    data-test-id="seat-chart-legend"
    @click="showLegendDialog = true"
  >
    <legend-seats-item
      v-if="!buttonOnly"
      :seat-chart-array="seatChartArray"
      :previous-seat-names="previousSeatNames"
      :seat-chart-options="seatChartOptions"
      :top-legend="!showFullLegend"
      :vertical="vertical"
      :showing="showing"
      :ticket-order-items="ticketOrderItems"
      :package-order-items="packageOrderItems"
      :packages="packages"
      :show-price="showPrice"
      @hover-seat="seat => $emit('hover-seat', seat)"
    />
    <q-btn
      v-if="!showFullLegend"
      class="col-auto"
      dense
      flat
      padding="xs"
      icon="far fa-circle-info"
      :label="$q.screen.lt.sm ? '' : $t('seatChart.legend')"
      :size="buttonOnly ? 'md' : 'sm'"
      data-test-id="seat-chart-legend-button"
    />

    <q-dialog v-model="showLegendDialog">
      <q-card
        class="legend"
        style="min-width: 300px; max-width: 500px"
      >
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{ $t('seatChart.legend') }}
          </div>
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>

        <q-card-section>
          <div
            class="column q-gutter-xs"
            :class="{
              'available-mode': availableMode,
              'selecting-mode': !availableMode
            }"
          >
            <legend-seats-item
              :seat-chart-array="seatChartArray"
              :previous-seat-names="previousSeatNames"
              :seat-chart-options="seatChartOptions"
              :vertical="vertical"
              :showing="showing"
              :ticket-order-items="ticketOrderItems"
              :package-order-items="packageOrderItems"
              :packages="packages"
              :show-price="showPrice"
              dialog
            />
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import SeatChart from 'src/models/screen/SeatChart'
import LegendSeatsItem from 'src/modules/admin/seat-chart/LegendSeatsItem'
import ShowingMixin from 'src/modules/checkout/components/mixins/ShowingMixin'
import TicketOrderItemsMixin from 'src/modules/register/components/mixins/TicketOrderItemsMixin'
export default {
  name: 'LegendSeats',
  components: { LegendSeatsItem },
  mixins: [TicketOrderItemsMixin, ShowingMixin],
  configuration: ['currentSite'],
  props: {
    seatChart: {
      type: SeatChart,
      required: true
    },
    seatChartOptions: {
      type: Object,
      required: true,
      default: () => ({})
    },
    seatPreferences: {
      type: Object,
      required: false,
      default: () => ({
        y_ratio: 0.5
      })
    },
    buttonOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    showFullLegend: {
      type: Boolean,
      required: false,
      default: false
    },
    vertical: {
      type: Boolean,
      required: false,
      default: false
    },

    order: {
      type: Object,
      required: false,
      default: null
    },
    noWrap: {
      type: Boolean,
      required: false,
      default: false
    },
    seatChartId: {
      type: Number,
      required: false,
      default: null
    }
  },
  emits: ['hover-seat'],
  data () {
    return {
      showLegendDialog: false
    }
  },
  computed: {
    showPrice () {
      // Check if the site is configured for ticket-first checkout
      const isTicketFirstCheckout = this.currentSite.consumerConfig.showTicketFirstCheckoutFlow
      // Check if any seat has limited ticket types
      const hasLimitedTicketTypes = _.some(_.flatten(this.seatChartArray), seat => !_.isNil(seat.areaCategoryId))
      // If new checkout is enabled, only show price if there are limited ticket types applied to any seat
      return !isTicketFirstCheckout && hasLimitedTicketTypes && (this.$consumer || this.$kiosk)
    },
    seatChartArray () {
      return this.seatChart.seatChart
    },
    previousSeats () {
      if (!this.seatPreferences.last_seats_for_chart) return

      return this.seatPreferences.last_seats_for_chart[this.seatChartId]
    },
    previousSeatNames () {
      return this.previousSeats?.seat_names || []
    }
  },
  watch: {
    'showing.loaded': {
      handler () {
        if (_.isNil(this.order) || _.isNil(this.showing)) return

        if (this.showing.loaded && this.showPrice) {
          this.refreshTransientOrder(true)
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.selecting-mode {
  :deep(.seat) {
    color: var(--q-seatAvailable);
    fill-opacity: 0;
  }
  :deep(.unavailable) {
    color: #AAA;
    stroke-dasharray: 15;
    fill-opacity: 0.2;
  }
  :deep(.reserved) {
    color: var(--q-negative);
    fill-opacity: 0.3;
    stroke-dasharray: 15;
  }
  :deep(.selected) {
    color: var(--q-seatSelected);
    fill-opacity: 0.4;
  }
  :deep(.selected.aisle) {
    fill: var(--q-seatSelected);
    border-width: 1px solid var(--q-seatSelected);
  }
  :deep(.selected-icon) {
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    border-radius: 50%;
    opacity: 1;
    padding: 5%;
    background-color: var(--q-seatSelected);
  }
}
.available-mode  {
  :deep(.seat) {
    color: var(--q-positive);
  }
  :deep(.reserved) {
    color: var(--q-primary);
  }
  :deep(.unavailable:not(.reserved)) {
    color: #AAA;
    stroke-dasharray: 15;
  }
}
.seat-swap-mode {
  :deep(.seat) {
    color: #AAA;
    .selected {
      color: var(--q-seatSelected) !important;
    }
    .source {
      color: var(--q-info) !important;
    }
    .reserved {
      color: var(--q-positive);
    }
    .cancelled {
      color: var(--q-negative);
      stroke-dasharray: 15;
    }
  }
}
.seat-swap-mode:not(.override) {
  pointer-events: none;
}
</style>
